import React, { useState } from 'react';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { CallbackModal } from '../../components/CallbackModal';
import { Calendar, Clock, Tag, ChevronLeft, MessageCircle, Plus, Minus, CheckCircle2, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-905">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-906">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-907">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-908">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-909" /> : <Plus className="w-4 h-4" data-id="element-910" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-911">
        <p className="text-gray-600 leading-relaxed" data-id="element-912">{answer}</p>
      </div>
    </div>;
}
export function VeneersGuideArticle() {
  const {
    lang
  } = useTranslation();
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const pick = (en: string, ua: string, ru: string, ro: string) => {
    if (lang === 'ua') return ua;
    if (lang === 'ru') return ru;
    if (lang === 'ro') return ro;
    return en;
  };
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: pick('Do veneers ruin teeth?', 'Чи псують вініри зуби?', 'Портят ли виниры зубы?', 'Fațetele strică dinții?'),
    a: pick('No, if the procedure is done correctly. We use minimally invasive techniques, removing only a thin layer of enamel (0.3-0.5 mm) to preserve the health of the tooth.', "Ні, якщо процедура виконана правильно. Ми використовуємо мінімально інвазивні техніки, знімаючи лише тонкий шар емалі (0.3-0.5 мм), щоб зберегти здоров'я зуба.", 'Нет, если процедура выполнена правильно. Мы используем минимально инвазивные техники, снимая лишь тонкий слой эмали (0.3-0.5 мм), чтобы сохранить здоровье зуба.', 'Nu, dacă procedura este efectuată corect. Folosim tehnici minim invazive, îndepărtând doar un strat subțire de smalț (0.3-0.5 mm) pentru a păstra sănătatea dintelui.')
  }, {
    q: pick('How much do veneers cost in London?', 'Скільки коштують вініри в Лондоні?', 'Сколько стоят виниры в Лондоне?', 'Cât costă fațetele în Londra?'),
    a: pick('Composite veneers cost from £250-£400 per tooth, ceramic — from £700-£1000. The price depends on the material and complexity.', 'Композитні вініри коштують від £250-£400 за зуб, керамічні — від £700-£1000. Ціна залежить від матеріалу та складності.', 'Композитные виниры стоят от £250-£400 за зуб, керамические — от £700-£1000. Цена зависит от материала и сложности.', 'Fațetele din compozit costă de la 250£-400£ pe dinte, cele ceramice — de la 700£-1000£. Prețul depinde de material și complexitate.')
  }, {
    q: pick('How long do veneers last?', 'Скільки служать вініри?', 'Сколько служат виниры?', 'Cât durează fațetele?'),
    a: pick('Ceramic veneers — 10-15 years or more. Composite — 5-7 years and require periodic polishing.', 'Керамічні вініри — 10-15 років і більше. Композитні — 5-7 років і потребують періодичного полірування.', 'Керамические виниры — 10-15 лет и более. Композитные — 5-7 лет и требуют периодической полировки.', 'Fațetele ceramice — 10-15 ani sau mai mult. Cele din compozit — 5-7 ani și necesită lustruire periodică.')
  }];
  return <Layout data-id="element-913">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-914" />

      <article className="bg-white" data-id="element-915">
        {/* Header */}
        <div className="bg-teal-50 py-12 lg:py-20" data-id="element-916">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-917">
            <Link to={`/${lang}/blog`} className="inline-flex items-center text-teal-600 font-medium mb-6 hover:underline" data-id="element-918">
              <ChevronLeft className="w-4 h-4 mr-1" data-id="element-919" />
              {pick('Back to blog', 'Назад до блогу', 'Назад в блог', 'Înapoi la blog')}
            </Link>

            <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-600" data-id="element-920">
              <span className="bg-white px-3 py-1 rounded-full font-medium text-teal-700 flex items-center shadow-sm" data-id="element-921">
                <Tag className="w-3 h-3 mr-2" data-id="element-922" />
                {pick('Aesthetics', 'Естетика', 'Эстетика', 'Estetică')}
              </span>
              <span className="flex items-center" data-id="element-923">
                <Calendar className="w-4 h-4 mr-2" data-id="element-924" />
                {pick('February 2026', 'Лютий 2026', 'Февраль 2026', 'Februarie 2026')}
              </span>
              <span className="flex items-center" data-id="element-925">
                <Clock className="w-4 h-4 mr-2" data-id="element-926" />
                {pick('9 min read', '9 хв читання', '9 мин чтения', '9 min citire')}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" data-id="element-927">
              {pick('Veneers in London — Everything you need to know', 'Вініри в Лондоні — все, що потрібно знати перед встановленням', 'Виниры в Лондоне — всё, что нужно знать перед установкой', 'Fațete în Londra — Tot ce trebuie să știți')}
            </h1>

            <div className="flex items-center" data-id="element-928">
              <div className="w-10 h-10 bg-teal-200 rounded-full flex items-center justify-center text-teal-700 font-bold mr-3" data-id="element-929">
                DC
              </div>
              <div data-id="element-930">
                <p className="font-medium text-gray-900" data-id="element-931">Dentico Clinic Team</p>
                <p className="text-sm text-gray-500" data-id="element-932">
                  {pick('Expert Article', 'Експертна стаття', 'Экспертная статья', 'Articol Expert')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-933">
          <div className="prose prose-lg prose-teal max-w-none text-gray-600" data-id="element-934">
            <p className="lead text-xl text-gray-800 font-medium mb-8" data-id="element-935">
              {pick('A perfect smile is a dream for many. Veneers allow you to change the shape, color, and position of teeth in a matter of weeks. But are they right for you? Which ones to choose — ceramic or composite? In this guide, we will answer all your questions.', 'Ідеальна посмішка — це мрія багатьох. Вініри дозволяють змінити форму, колір та положення зубів за лічені тижні. Але чи підходять вони вам? Які обрати — керамічні чи композитні? У цьому гіді ми відповімо на всі питання.', 'Идеальная улыбка — это мечта многих. Виниры позволяют изменить форму, цвет и положение зубов за считанные недели. Но подходят ли они вам? Какие выбрать — керамические или композитные? В этом гиде мы ответим на все вопросы.', 'Un zâmbet perfect este un vis pentru mulți. Fațetele vă permit să schimbați forma, culoarea și poziția dinților în câteva săptămâni. Dar sunt ele potrivite pentru dumneavoastră? Pe care să le alegeți — ceramice sau compozite? În acest ghid, vom răspunde la toate întrebările dumneavoastră.')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-936">
              {pick('What are veneers?', 'Що таке вініри?', 'Что такое виниры?', 'Ce sunt fațetele?')}
            </h2>
            <p className="mb-6" data-id="element-937">
              {pick('Veneers are thin shells (similar to contact lenses) that are fixed to the front surface of the teeth. They mask imperfections: chips, stains, gaps (diastemas), or minor misalignments.', 'Вініри — це тонкі накладки (схожі на контактні лінзи), які фіксуються на передню поверхню зубів. Вони маскують недоліки: сколи, плями, проміжки (діастеми) або незначні викривлення.', 'Виниры — это тонкие накладки (похожие на контактные линзы), которые фиксируются на переднюю поверхность зубов. Они маскируют недостатки: сколы, пятна, промежутки (диастемы) или незначительные искривления.', 'Fațetele sunt învelișuri subțiri (similare lentilelor de contact) care sunt fixate pe suprafața frontală a dinților. Ele maschează imperfecțiunile: ciobituri, pete, spații (diasteme) sau alinieri minore.')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-938">
              {pick('Types of veneers: Porcelain vs Composite', 'Типи вінірів: Порцеляна vs Композит', 'Типы виниров: Фарфор vs Композит', 'Tipuri de fațete: Porțelan vs Compozit')}
            </h2>

            <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-939">
              <div className="bg-gray-50 p-6 rounded-xl" data-id="element-940">
                <h3 className="text-xl font-bold text-teal-700 mb-4 flex items-center" data-id="element-941">
                  <Sparkles className="w-5 h-5 mr-2" data-id="element-942" />
                  {pick('Ceramic (Porcelain)', 'Керамічні (Порцелянові)', 'Керамические (Фарфоровые)', 'Ceramice (Porțelan)')}
                </h3>
                <ul className="space-y-2 text-sm" data-id="element-943">
                  <li data-id="element-944">
                    ✅{' '}
                    {pick('Look highly natural', 'Виглядають максимально природно', 'Выглядят максимально естественно', 'Arată foarte natural')}
                  </li>
                  <li data-id="element-945">
                    ✅{' '}
                    {pick('Do not change color', 'Не змінюють колір з часом', 'Не меняют цвет со временем', 'Nu își schimbă culoarea')}
                  </li>
                  <li data-id="element-946">
                    ✅{' '}
                    {pick('Very strong and durable', 'Дуже міцні та довговічні', 'Очень прочные и долговечные', 'Foarte puternice și durabile')}
                  </li>
                  <li data-id="element-947">
                    ❌{' '}
                    {pick('More expensive', 'Дорожчі', 'Дороже', 'Mai scumpe')}
                  </li>
                </ul>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl" data-id="element-948">
                <h3 className="text-xl font-bold text-teal-700 mb-4 flex items-center" data-id="element-949">
                  <Sparkles className="w-5 h-5 mr-2" data-id="element-950" />
                  {pick('Composite (Bonding)', 'Композитні (Bonding)', 'Композитные (Bonding)', 'Compozit (Bonding)')}
                </h3>
                <ul className="space-y-2 text-sm" data-id="element-951">
                  <li data-id="element-952">
                    ✅ {pick('Cheaper', 'Дешевші', 'Дешевле', 'Mai ieftine')}
                  </li>
                  <li data-id="element-953">
                    ✅{' '}
                    {pick('Installed in 1 visit', 'Встановлюються за 1 візит', 'Устанавливаются за 1 визит', 'Instalate într-o singură vizită')}
                  </li>
                  <li data-id="element-954">
                    ✅{' '}
                    {pick('Easily repaired', 'Легко ремонтуються', 'Легко ремонтируются', 'Ușor de reparat')}
                  </li>
                  <li data-id="element-955">
                    ❌{' '}
                    {pick('Less durable', 'Менш довговічні', 'Менее долговечные', 'Mai puțin durabile')}
                  </li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-956">
              {pick('Why Dentico Clinic?', 'Чому Dentico Clinic?', 'Почему Dentico Clinic?', 'De ce Dentico Clinic?')}
            </h2>
            <p className="mb-6" data-id="element-957">
              {pick('We specialize in aesthetic dentistry. Our goal is to create a smile that looks natural, not like "fake white teeth". We take into account the proportions of your face, facial expressions, and wishes.', 'Ми спеціалізуємося на естетичній стоматології. Наша мета — створити посмішку, яка виглядає натурально, а не як "штучні білі зуби". Ми враховуємо пропорції вашого обличчя, міміку та побажання.', 'Мы специализируемся на эстетической стоматологии. Наша цель — создать улыбку, которая выглядит натурально, а не как "искусственные белые зубы". Мы учитываем пропорции вашего лица, мимику и пожелания.', 'Ne specializăm în stomatologia estetică. Scopul nostru este de a crea un zâmbet care arată natural, nu ca "dinți albi falși". Luăm în considerare proporțiile feței dumneavoastră, expresiile faciale și dorințele.')}
            </p>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 pt-10 border-t border-gray-100" data-id="element-958">
            <h3 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-959">
              {pick('Frequently Asked Questions', 'Часті запитання', 'Частые вопросы', 'Întrebări Frecvente')}
            </h3>
            <div className="space-y-2" data-id="element-960">
              {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-961" />)}
            </div>
          </div>

          {/* Read Also */}
          <div className="mt-16" data-id="element-962">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-963">
              {pick('Read Also', 'Читайте також', 'Читайте также', 'Citește și')}
            </h3>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-964">
              <Link to={`/${lang}/veneers-london`} data-id="element-965">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-966">
                  <span className="text-teal-600 font-medium" data-id="element-967">
                    {pick('Veneers at Dentico Clinic', 'Вініри в Dentico Clinic', 'Виниры в Dentico Clinic', 'Fațete la Dentico Clinic')}
                  </span>
                </Card>
              </Link>
              <Link to={`/${lang}/services/smile-makeover`} data-id="element-968">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-969">
                  <span className="text-teal-600 font-medium" data-id="element-970">
                    Smile Makeover
                  </span>
                </Card>
              </Link>
            </div>
          </div>
        </div>

        {/* CTA */}
        <section className="py-16 bg-teal-900" data-id="element-971">
          <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-972">
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-973">
              {pick('Time for your perfect smile', 'Час для вашої ідеальної посмішки', 'Время для вашей идеальной улыбки', 'Timpul pentru zâmbetul tău perfect')}
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-974">
              {pick('Book a consultation and try on your future smile.', 'Запишіться на консультацію та примірку майбутньої посмішки.', 'Запишитесь на консультацию и примерку будущей улыбки.', 'Programați o consultație și probați viitorul dumneavoastră zâmbet.')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-975">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-976">
                <Button size="lg" className="w-full gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-977">
                  <Calendar className="w-5 h-5" data-id="element-978" />
                  {pick('Book Online', 'Записатись онлайн', 'Записаться онлайн', 'Programare Online')}
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-979">
                <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-980">
                  <MessageCircle className="w-5 h-5" data-id="element-981" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>
      </article>
    </Layout>;
}